import api from '../index';
import { LoginCredentials, RegisterCredentials } from '../../types/auth';
import { User, ResponseLogin } from '../../types/user';
import { message } from 'antd';

export const authService = {
    async login(email: string, password: string): Promise<ResponseLogin> {
        // First get CSRF cookie
        await api.get('/sanctum/csrf-cookie');
        
        // Then perform login
        const response = await api.post<ResponseLogin>('/login', { 
            email, 
            password 
        }).catch((error) => {
            if (error.response?.status === 401) {
                message.error('Pogresan email ili lozinka');
            }
            else if (error.response?.status === 403) {
                message.error('Niste verifikovali email ili niste odobreni od strane administratora');
            }
            else if (error.response?.status === 422) {
                message.error('Uneti podaci nisu validni');
            }
            
            throw error;
        }
        );
        
        // Store user data and token
        if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
            localStorage.setItem('token', response.data.token);
        }
        if (response.data.message) {
            message.success(response.data.message);
        }
        
        return response.data;
    },

    async register(data: RegisterCredentials): Promise<{ message: string; approval_required: boolean }> {
        // Get CSRF cookie first
        await api.get('/sanctum/csrf-cookie');
        
        // Perform registration
        const response = await api.post<{ message: string; approval_required: boolean }>('/register', data);
        return response.data;
    },

    async logout(): Promise<void> {
        try {
            await api.post('/logout');
        } finally {
            // Always clear local storage, even if API call fails
            localStorage.removeItem('user');
            localStorage.removeItem('token');
        }
    },

    async getCurrentUser(): Promise<User | null> {
        try {
            const response = await api.get<{ user: User }>('/user');
            const user = response.data.user;
            
            // Update stored user data
            localStorage.setItem('user', JSON.stringify(user));
            
            return user;
        } catch (error) {
            // If request fails, clear stored data
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            return null;
        }
    },

    async checkAuth(): Promise<boolean> {
        try {
            await this.getCurrentUser();
            return true;
        } catch {
            return false;
        }
    },

    async resendVerification(email: string): Promise<void> {
        await api.post('/email/verification-notification', { email });
    },

    async requestPasswordReset(email: string): Promise<void> {
        await api.post('/forgot-password', { email });
    },

    async resetPassword(data: {
        email: string;
        password: string;
        password_confirmation: string;
        token: string;
    }): Promise<void> {
        await api.post('/reset-password', data);
    },

    async updateProfile(data: Partial<User>): Promise<User> {
        const response = await api.put<User>('/user/profile', data);
        const updatedUser = response.data;
        
        // Update stored user data
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        return updatedUser;
    },

    async changePassword(data: {
        current_password: string;
        password: string;
        password_confirmation: string;
    }): Promise<void> {
        await api.put('/user/password', data);
    }
};

export default authService;
